import { useId } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/shared/ui';

export interface DateFieldValue {
  start: string | null;
  end: string | null;
}

export interface DateFieldConfig {
  format?: string;
}

/**
 * The template builder writes exactly one of two strings, and the server's `@IsIn` validates
 * them, so the range pair is gated on the range value rather than on "not the single value".
 * Gating the other way round made an absent or unrecognised format open the To input, which
 * captures an end date the field was never configured to hold.
 */
// eslint-disable-next-line i18next/no-literal-string -- wire value: DateSectionConfigDto validates this exact string server-side
const RANGE_FORMAT = 'Start + End date';

interface DateFieldProps {
  value: DateFieldValue | null;
  config: DateFieldConfig;
  disabled?: boolean;
  onChange: (next: DateFieldValue) => void;
}

/**
 * `DatePicker.Trigger` only checks for `null` before formatting, and `format()` on an Invalid
 * Date throws a RangeError. With no ErrorBoundary under this page that blanks the whole brief,
 * so an unparseable string degrades to "no date" here as well as at the Zod boundary: this is
 * the last point before the throw, and it must hold whatever route the value arrived by.
 */
const toDate = (iso: string | null): Date | null => {
  if (!iso) return null;
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed : null;
};
const toIso = (date: Date | null): string | null => (date ? format(date, 'yyyy-MM-dd') : null);

/**
 * Pure, controlled — no hooks beyond local i18n, no capability reads (those live in
 * `BriefSectionShell`). Only the range format renders a From/To pair, per the prototype and
 * the spec's date-range scenario; every other config renders a single input.
 *
 * `DatePicker.Trigger` doesn't forward `aria-label`, so each picker gets its own `id` +
 * visually-hidden `<label htmlFor>` for a real programmatic label association instead.
 */
export function DateField({ value, config, disabled = false, onChange }: DateFieldProps) {
  const { t } = useTranslation('projects');
  const startId = useId();
  const endId = useId();
  const isRangeMode = config.format === RANGE_FORMAT;
  const start = value?.start ?? null;
  const end = value?.end ?? null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div>
          <label htmlFor={startId} className="sr-only">
            {t('projectBriefDetail.briefSectionField.dateField.startDate')}
          </label>
          <DatePicker
            id={startId}
            value={toDate(start)}
            disabled={disabled}
            onChange={(next) => onChange({ start: toIso(next), end: isRangeMode ? end : null })}
          >
            <DatePicker.Trigger />
            <DatePicker.Content />
          </DatePicker>
        </div>
        {isRangeMode && (
          <div>
            <label htmlFor={endId} className="sr-only">
              {t('projectBriefDetail.briefSectionField.dateField.endDate')}
            </label>
            <DatePicker
              id={endId}
              value={toDate(end)}
              disabled={disabled}
              onChange={(next) => onChange({ start, end: toIso(next) })}
            >
              <DatePicker.Trigger />
              <DatePicker.Content />
            </DatePicker>
          </div>
        )}
      </div>
    </div>
  );
}
